import {
  Box,
  ColorSwatch,
  ProportionBar,
  Skeleton,
  Text,
  WidgetDisclosureRow,
  WidgetDisclosureRowSkeleton,
  type WidgetListColumn,
} from "@salestrak/ui";
import { type OutcomeBreakdownRow } from "./use-outcome-performance";
import {
  FIRST_PAINT_MEMBER_ROWS,
  MEMBER_BAR_PX,
  MEMBER_COUNT_PX,
  MEMBER_NAME_COMPACT_PX,
  MEMBER_NAME_PX,
  MEMBER_RATE_PX,
  TONE_COLORS,
  TONE_ORDER,
  type ToneKey,
  shareFigure,
} from "./vocabulary";

/** The list's two figure columns: the Updates count, then the outcome's
 *  share of the Updates that have one — the legend's own figure, per word. */
export const OUTCOME_COLUMNS: WidgetListColumn[] = [
  { key: "updates", label: "Updates", width: MEMBER_COUNT_PX },
  { key: "share", label: "Share", width: MEMBER_RATE_PX },
];

/**
 * The outcome words as list rows, in the legend's tone order and largest
 * first inside a tone, the missing bucket left out — the row under the legend
 * is its place. The list is what the member list is on a team's board: the
 * detail the mix summarizes, filling the height the board gave the card.
 */
export function outcomeListRows(outcomes: readonly OutcomeBreakdownRow[]): OutcomeBreakdownRow[] {
  const rank = new Map<string, number>(TONE_ORDER.map((tone, index) => [tone, index]));
  return outcomes
    .filter((row) => row.tone !== "missing")
    .sort((a, b) => (rank.get(a.tone) ?? 0) - (rank.get(b.tone) ?? 0) || b.count - a.count);
}

function OutcomeBarRow({
  row,
  compact,
  maxCount,
  completed,
  focus,
  selected,
  onClick,
}: {
  row: OutcomeBreakdownRow;
  compact: boolean;
  maxCount: number;
  completed: number;
  focus: ToneKey | null;
  selected: boolean;
  onClick: () => void;
}) {
  const [countColumn, shareColumn] = OUTCOME_COLUMNS;
  return (
    <WidgetDisclosureRow expanded={selected} label={`${row.label} Project Updates`} onToggle={onClick}>
      <Box direction="row" align="center" gap="md" fill="flex" shrink minWidth={0}>
        <Box
          direction="row"
          align="center"
          gap="xs"
          width={compact ? MEMBER_NAME_COMPACT_PX : MEMBER_NAME_PX}
          shrink={compact}
          minWidth={0}
        >
          <ColorSwatch color={TONE_COLORS[row.tone]} size="xs" rounded="full" />
          <Text size="sm" weight="medium" truncate>
            {row.label}
          </Text>
        </Box>
        <Box fill="flex" minWidth={0}>
          {/* One segment, the row's tone, so a tone focused in the legend
              lights these rows and dims the rest exactly as it does the
              member bars. */}
          <ProportionBar
            segments={[{ id: row.tone, label: row.label, value: row.count }]}
            colorTokens={[TONE_COLORS[row.tone]]}
            thickness={MEMBER_BAR_PX}
            span={maxCount > 0 ? row.count / maxCount : 0}
            focusedId={focus}
          />
        </Box>
      </Box>
      <Box width={countColumn?.width} shrink={false} align="end">
        <Text size="sm" weight="medium" variant="muted" tabular>
          {row.count.toLocaleString()}
        </Text>
      </Box>
      <Box width={shareColumn?.width} shrink={false} align="end">
        <Text size="sm" weight="semibold" tabular>
          {shareFigure(row.count, completed)}
        </Text>
      </Box>
    </WidgetDisclosureRow>
  );
}

export function OutcomeBars({
  rows,
  compact,
  completed,
  focus,
  onOutcomeClick,
  selectedOutcomeId,
}: {
  rows: OutcomeBreakdownRow[];
  compact: boolean;
  /** The Updates with an outcome — every share's base. */
  completed: number;
  focus: ToneKey | null;
  onOutcomeClick: (row: OutcomeBreakdownRow) => void;
  selectedOutcomeId?: string | undefined;
}) {
  const maxCount = rows.reduce((max, row) => Math.max(max, row.count), 0);
  return (
    <Box direction="column">
      {rows.map((row) => (
        <OutcomeBarRow
          key={row.outcomeId}
          row={row}
          compact={compact}
          maxCount={maxCount}
          completed={completed}
          focus={focus}
          selected={selectedOutcomeId === row.outcomeId}
          onClick={() => onOutcomeClick(row)}
        />
      ))}
    </Box>
  );
}

/** The rows while their figures are in flight — one per outcome the last
 *  answer listed (three before any answer), at the row's own geometry. */
export function OutcomeBarsSkeleton({
  rows,
  compact,
  selectedOutcomeId,
}: {
  rows: OutcomeBreakdownRow[];
  compact: boolean;
  selectedOutcomeId?: string | undefined;
}) {
  const keys =
    rows.length > 0
      ? rows.map((row) => row.outcomeId)
      : Array.from({ length: FIRST_PAINT_MEMBER_ROWS }, (_, index) => `slot-${index}`);
  return (
    <Box direction="column">
      {keys.map((key) => (
        <WidgetDisclosureRowSkeleton key={key} selected={key === selectedOutcomeId}>
          <Box direction="row" align="center" gap="md" fill="flex" shrink minWidth={0}>
            <Box
              width={compact ? MEMBER_NAME_COMPACT_PX : MEMBER_NAME_PX}
              shrink={compact}
              minWidth={0}
              direction="row"
              align="center"
              gap="xs"
            >
              <Skeleton delayed circle width={8} />
              <Skeleton delayed height={10} width="60%" />
            </Box>
            <Box fill="flex" minWidth={0}>
              <Skeleton delayed height={MEMBER_BAR_PX} width="50%" />
            </Box>
          </Box>
          {OUTCOME_COLUMNS.map((column) => (
            <Box key={column.key} width={column.width} shrink={false} align="end">
              <Skeleton delayed height={10} width={24} />
            </Box>
          ))}
        </WidgetDisclosureRowSkeleton>
      ))}
    </Box>
  );
}
